import type {
    AxiosError, AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios';
import { useUserStore } from '@/stores/user';
import type { AuthenticationResponse } from '@/types/Response/AuthenticationResponse';
import { AuthenticationRoutes } from '@/services/routes/AuthenticationRoutes';
import AbstractMiddleware from '@/services/middleware/AbstractMiddleware';
import type { AxiosErrorMiddleware } from '@/services/middleware/AxiosMiddleware';

class RefreshTokenMiddleware extends AbstractMiddleware implements AxiosErrorMiddleware {
    private refreshed!: boolean;

    /* eslint-disable-next-line class-methods-use-this */
    public canHandleError = (error: AxiosError): boolean => {
        const status = error.response?.status;
        return status === 401;
    };

    /* eslint-disable-next-line consistent-return */
    public onError = async (error: AxiosError): Promise<AxiosResponse|undefined> => {
        const userStore = useUserStore();

        // Attempt to refresh the current token
        if (!this.refreshed) {
            const previousRequest: InternalAxiosRequestConfig|undefined = error.config;

            /** Prevent Axios from auto formatting as JSON */
            if (previousRequest?.data instanceof FormData) {
                previousRequest.headers?.setContentType('multipart/form-data', true);
            }

            this.refreshed = true;
            const data: AuthenticationResponse = await this.axiosInstance
                .get(AuthenticationRoutes.REFRESH);
            if (data) {
                userStore.authenticate(data);
                if (
                    previousRequest
                    && previousRequest.url !== AuthenticationRoutes.REFRESH
                    && previousRequest.url !== AuthenticationRoutes.LOGIN
                ) {
                    this.refreshed = false;
                    return this.axiosInstance.request(previousRequest);
                }
            }
        // Otherwise, unauthorized attempt to refresh, log user out.
        } else {
            userStore.logout();
            await this.axiosInstance.post(AuthenticationRoutes.LOGOUT);
            window.location.replace(`${import.meta.env.VITE_FSUM_APP_URL}/account?courseLoc=crosscheck`);
        }
    };
}

export { RefreshTokenMiddleware };
